import React from 'react';
import { Box, CssBaseline } from '@mui/material';

import { Component, PlateTypeProperties } from '@paragon/contract-library';
import { LoginContainer } from '@paragon/login-tools';
import { NumericField, ParagonHeader } from '@paragon/material';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { UserDropdown } from '@paragon/themes';
import { PhysicalComponentVisual, ThreeJsScene } from '@paragon/three-js-truss-scene';

import { ComponentDesignAndTransformationMatrix, Truckload } from '../data/componentsConnection';
import { setDisplayedTruckload } from '../store/packingSlice';
import { FileImport } from './FileImport';
import { useAppDispatch, useAppSelector } from './hooks';
import TruckCapacity from './TruckCapacity';

const canvasWidth = 1024;
const canvasHeight = 512;

const renderInsert = (
  component: Component,
  plateTypeProperties: { [getPlateTypeStringResult: string]: PlateTypeProperties | undefined },
  transformationMatrix: readonly number[],
) => (
  <PhysicalComponentVisual
    key={component.guid}
    members={component.members}
    platePairs={component.platePairs}
    plateTypeProperties={plateTypeProperties}
    component={component}
    matrix={transformationMatrix}
    showComponentLabel
  />
);

const renderComponentDesign = (componentDesignAndTransformationMatrices: ComponentDesignAndTransformationMatrix) => (
  <group key={componentDesignAndTransformationMatrices.componentDesign.guid}>
    {componentDesignAndTransformationMatrices.transformationMatrices.map((transformationMatrix) =>
      renderInsert(
        componentDesignAndTransformationMatrices.componentDesign.component,
        componentDesignAndTransformationMatrices.plateTypeProperties,
        transformationMatrix,
      ),
    )}
  </group>
);

const renderTruckload = (truckload: Truckload, index: number, visible: boolean) => (
  <group key={index} visible={visible}>
    {truckload.componentDesignsAndTransformationMatrices.map((componentDesignAndTransformationMatrices) =>
      renderComponentDesign(componentDesignAndTransformationMatrices),
    )}
    <TruckCapacity width={truckload.length} height={truckload.width} depth={truckload.height} />
  </group>
);

const renderThreeJsScene = (truckloads: readonly Truckload[], visibleIndex: number) => (
  <div className="file-set-scene">
    <ThreeJsScene width={canvasWidth} height={canvasHeight}>
      {truckloads.map((truckload, index) => renderTruckload(truckload, index, index === visibleIndex))}
    </ThreeJsScene>
  </div>
);

export function Root() {
  const dispatch = useAppDispatch();

  const spinnerMessages = useAppSelector(getSpinnersToDisplay);
  const truckloads = useAppSelector((state) => state.packing.truckloads);
  let truckloadIndex = useAppSelector((state) => state.packing.displayedTruckloadIndex);

  let truckloadViewer = null;

  if (truckloads.length) {
    if (truckloadIndex < 0) {
      truckloadIndex = 0;
    }
    if (truckloadIndex >= truckloads.length) {
      truckloadIndex = truckloads.length - 1;
    }

    const changeHandler = (value: number) => {
      dispatch(setDisplayedTruckload(value - 1));
    };

    truckloadViewer = (
      <div>
        <NumericField
          label="Truckload index"
          min={1}
          max={truckloads.length}
          value={truckloadIndex + 1}
          onSubmit={changeHandler}
          fullWidth={false}
          sx={{ minWidth: 100 }}
          integers
        />

        {renderThreeJsScene(truckloads, truckloadIndex)}
      </div>
    );
  }

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          position: 'absolute',

          width: '100%',
          height: '100%',

          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoginContainer requirePermission="packing-access">
          <ParagonHeader text="Paragon Packing">
            <UserDropdown />
          </ParagonHeader>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              height: '100%',
              // Typically the Root Component uses the canvas.main as the background
              bgcolor: 'canvas.main',
              padding: 3,
              minWidth: 0, // So the Typography noWrap works
            }}
          >
            {Array.from(spinnerMessages).map((text) => (
              <SpinnerMessage text={text} key={text} />
            ))}

            <FileImport />
            {truckloadViewer}
          </Box>
        </LoginContainer>
      </Box>
    </>
  );
}
